import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {MediaMatcher} from '@angular/cdk/layout';
import {StartupService} from '../../service-ui/startup.service';
import {OrganizationService} from '../../service-data/organization.service';
import {FormControl} from '@angular/forms';
import {Observable, forkJoin, of} from 'rxjs';
import {catchError, map, startWith} from 'rxjs/operators';
import {LanguagePipe} from '../../pipe/language.pipe';

/**
 * Row shape shared with `OrganizationService.getActiveOrganizationsForRole`.
 *
 * `admin_ID` + `adminEmail` are OPTIONAL — added 2026-08-06 so
 * operators can search the picker by the primary user's email
 * address (typically the easiest thing they remember about a
 * customer). Older server builds don't return the fields; when
 * they're absent we simply omit the email line and search stays
 * name/ID-only for that row. `admin_ID` is included so future
 * flows that need to link straight into the admin user's record
 * don't have to re-query.
 */
type ActiveOrgRow = {
  ID: number;
  name: string;
  serviceProvider_ID: number;
  admin_ID?: number;
  adminEmail?: string;
};

/**
 * Chromeless landing page shown right after Cognito auth when the
 * signed-in user has more than one role option. Purpose:
 *
 * 1. Present the "primary" roles the user can act as (their own
 *    user role plus any `network` / `facility` / `serviceProvider`
 *    / `umbrella` / `organization` role from
 *    `startupService.roleOptions`) as big buttons that mirror the
 *    icons the `SideNavComponent` uses.
 * 2. If the user holds a `network` role — call
 *    `OrganizationService.getActiveOrganizationsForRole('network', roleId)`
 *    once to pull the full list of active organizations under that
 *    network.
 * 3. If the user has no `network` role but has one or more
 *    `serviceProvider` roles — call the same endpoint once per
 *    `serviceProvider` and merge the results.
 * 4. Present the merged organization list in a searchable
 *    autocomplete (filterable by name or ID). Once an org is
 *    picked, the operator clicks "Proceed" and we call
 *    `startupService.selectRole('organization', <ID>, <name>)`
 *    which triggers the normal role-scoped data-load pipeline.
 *
 * Two things distinguish this component from the rest of the
 * secure UI:
 * - It renders **without** the SideNav or the top toolbar chrome.
 *   To achieve that it is routed as a top-level route rather than
 *   under `SecureHomeComponent`.
 * - It **does not trigger any data services to load**. The only
 *   HTTP calls it makes are the lightweight `activeForRole`
 *   lookups above; the full role-scoped data load only starts
 *   AFTER the operator picks a role via `selectRole(...)` — which
 *   is exactly what `StartupService.selectRole` already does.
 */
@Component({
  selector: 'app-select-role',
  templateUrl: './select-role.component.html',
  styleUrls: ['./select-role.component.css']
})
export class SelectRoleComponent implements OnInit, OnDestroy {
  /** Reactive media query so the toolbar/sidenav shell can flip
   *  into `over` mode on narrow viewports — mirrors the pattern in
   *  `StartupComponent`. */
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  // ── Primary role buttons ─────────────────────────────
  /**
   * Ordered list of icon + label metadata by role name — mirrors
   * the icons used inside `SideNavComponent` so the two surfaces
   * feel like the same picker.
   *
   * `label` is the **English source text**, which doubles as the
   * `LanguagePipe` lookup key — see `labelFor`. It is deliberately
   * not the raw role name (`serviceProvider`), because the
   * registered translation ids are the display forms
   * (`@@Service Provider`, `@@Facility`, …).
   */
  roleMeta: { [key: string]: { icon: string; label: string; order: number } } = {
    network:         { icon: 'device_hub',       label: 'Network',           order: 1 },
    facility:        { icon: 'home_work',        label: 'Facility',          order: 2 },
    serviceProvider: { icon: 'room_service',     label: 'Service Provider',  order: 3 },
    umbrella:        { icon: 'beach_access',     label: 'Umbrella',          order: 4 },
    organization:    { icon: 'handyman',         label: 'Organization',      order: 5 }
  };

  /**
   * Resolved (translated) label per role name.
   *
   * `labelFor` is called from the template, so it runs on every
   * change-detection pass. `LanguagePipe.get` console.warns on a
   * miss, and an unmapped role (anything Cognito emits that isn't in
   * `roleMeta` — `resource`, say) would otherwise produce one warning
   * per tick. Caching also keeps the lookup off the hot path.
   */
  private roleLabelCache: { [role: string]: string } = {};

  // ── Organization dropdown ────────────────────────────
  /**
   * Merged list of active organizations pulled via the role-scoped
   * `activeForRole` endpoint. Deduplicated by `ID` so an org that
   * belongs to multiple queried service providers appears once.
   */
  organizations: Array<ActiveOrgRow> = [];

  /** True while the network / serviceProvider lookups are in flight. */
  loadingOrganizations = false;

  /** Error message shown if the org fetch fails; UI degrades gracefully. */
  organizationsError = '';

  /**
   * Per-role lookup failures captured across the fan-out. The
   * combobox stays populated with whatever DID succeed; anything
   * in here powers the "Retry" affordance so a single flaky
   * request doesn't hide the roles that worked. Keyed by the
   * label the operator will actually recognise
   * (`network #12`, `serviceProvider #7`, …).
   */
  organizationLoadFailures: Array<{ role: string; roleId: string; label: string; message: string }> = [];

  /** Reactive form control backing the searchable autocomplete input. */
  organizationSearchControl = new FormControl<string>('');

  /** Filtered list observed by the template based on the search text. */
  filteredOrganizations$: Observable<Array<ActiveOrgRow>>;

  /** The org currently picked in the autocomplete; `null` when none. */
  selectedOrganization: ActiveOrgRow | null = null;

  constructor(
    public startupService: StartupService,
    private organizationService: OrganizationService,
    private changeDetectorRef: ChangeDetectorRef,
    private media: MediaMatcher
  ) {
    this.mobileQuery = this.media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnDestroy(): void {
    if (this.mobileQuery && this._mobileQueryListener) {
      this.mobileQuery.removeListener(this._mobileQueryListener);
    }
  }

  ngOnInit(): void {
    console.log('SelectRoleComponent.ngOnInit: roleOptions=', this.startupService.roleOptions,
      'defaultRole=', this.startupService.getRole(), 'defaultRoleId=', this.startupService.getRoleId());
    // Live-filter as the user types. `startWith('')` primes the
    // observable so the panel shows the full list on first open.
    this.filteredOrganizations$ = this.organizationSearchControl.valueChanges.pipe(
      startWith(''),
      map(v => this.filterOrganizations(v || ''))
    );

    // Note: the "effectively single option" auto-proceed used to
    // live here but caused a visible flash of this component when
    // `setJwtToken(...)` navigated here for what turned out to be a
    // single-option JWT. That gate now lives in
    // `StartupService.setJwtToken(...)` — it dedupes `roleOptions`
    // and routes straight to `/secure/startup` when only one role
    // is actually available. Reaching this component now always
    // means the operator has a real choice to make (either from a
    // multi-option JWT or by clicking the "Select Role" SideNav
    // entry deliberately), so we render the picker unconditionally.

    this.loadOrganizationsForRoles();
  }

  // ───────────────────────────────────────────────────────
  // Role button plumbing
  // ───────────────────────────────────────────────────────

  /**
   * Order + de-duplicate `startupService.roleOptions` for the
   * button row. `organization` role options are also surfaced as
   * buttons (in case the user only has organization roles) — the
   * autocomplete is an alternative path for admins with a `network`
   * or `serviceProvider` role wanting to impersonate a specific
   * organization.
   *
   * The currently-active role/roleId is filtered out: clicking it
   * would call `StartupService.selectRole(...)` which early-returns
   * when the requested combo matches the selected combo (no
   * navigation, no data reload), leaving the operator staring at a
   * dead button. Hiding it makes the picker offer only actionable
   * choices — which is exactly what the operator opened this page
   * to look for.
   */
  get orderedRoleOptions(): Array<{ role: string; roleId: string }> {
    const opts = (this.startupService.roleOptions || []).slice()
      .filter(o => !(o && this.isSelectedRoleButton(o.role, o.roleId)));
    return opts.sort((a, b) => {
      const ao = this.roleMeta[a.role] ? this.roleMeta[a.role].order : 99;
      const bo = this.roleMeta[b.role] ? this.roleMeta[b.role].order : 99;
      if (ao !== bo) { return ao - bo; }
      return String(a.roleId).localeCompare(String(b.roleId));
    });
  }

  /**
   * True when the JWT-default "My User" button represents the
   * currently-active role/roleId — used by the template to hide it
   * for the same reason `orderedRoleOptions` filters selected
   * entries out (`selectRole` would no-op).
   */
  get isDefaultUserRoleCurrent(): boolean {
    return this.isSelectedRoleButton(this.startupService.getRole(), this.startupService.getRoleId());
  }

  iconFor(role: string): string {
    return (this.roleMeta[role] && this.roleMeta[role].icon) || 'account_box';
  }

  /**
   * Translated display label for a role.
   *
   * Known roles resolve through their `roleMeta.label` (which is the
   * registered translation id — `Network`, `Facility`,
   * `Service Provider`, `Umbrella`, `Organization`). Anything else
   * falls back to looking the raw role name up directly, so a role
   * Cognito hands us that isn't in `roleMeta` (`resource`, for
   * instance) still gets translated if a span exists for it, and
   * degrades to the raw name if one doesn't.
   */
  labelFor(role: string): string {
    if (!role) { return ''; }
    if (this.roleLabelCache[role] === undefined) {
      const source = (this.roleMeta[role] && this.roleMeta[role].label) || role;
      this.roleLabelCache[role] = LanguagePipe.get(source);
    }
    return this.roleLabelCache[role];
  }

  /**
   * Role ids are rendered as `matBadge` values and inside `title`
   * strings — neither of which goes through a pipe — so they need the
   * digit table applied explicitly or an Arabic UI shows Western
   * numerals next to Arabic-Indic ones elsewhere on the same screen.
   */
  localizeNumber(value: any): string {
    return LanguagePipe.localizeNumber(value);
  }

  /** `Service Provider #7` with the id in locale digits. */
  titleForRoleOption(roleOption: { role: string; roleId: string }): string {
    return this.labelFor(roleOption.role) + ' #' + this.localizeNumber(roleOption.roleId);
  }

  isSelectedRoleButton(role: string, roleId: string): boolean {
    return this.startupService.selectedRole === role && this.startupService.selectedRoleId === roleId;
  }

  selectRoleOption(role: string, roleId: string): void {
    // Mirrors the SideNav pattern: pass roleId as roleName until
    // a nicer name is available. `selectRole` navigates to
    // `/secure/startup` and kicks off the data load.
    this.startupService.selectRole(role, roleId, roleId);
  }

  /**
   * Fallback "sign in as my own user" button — matches the first
   * button in the SideNav role picker.
   */
  selectDefaultUserRole(): void {
    const roleId = this.startupService.getRoleId();
    this.startupService.selectRole(this.startupService.getRole(), roleId, roleId);
  }

  // ───────────────────────────────────────────────────────
  // Organization autocomplete
  // ───────────────────────────────────────────────────────

  /**
   * Kick off the role-scoped active-org lookups per the rules the
   * user described:
   * - If any role option is `network` → call the endpoint once with
   *   that network role. (Networks see every org they own; querying
   *   per serviceProvider on top of that is redundant.)
   * - Else, for every `serviceProvider` role option → call the
   *   endpoint once per service provider and merge the results.
   * - Otherwise (user only holds facility / organization / umbrella
   *   roles) → no autocomplete; the section is hidden.
   *
   * Each lookup is materialized individually so a single 401 / 403
   * / 5xx on one role doesn't wipe out results from the roles that
   * DID succeed — the combobox stays useful and the failing role
   * gets a retry button. Prior to this change the shared
   * `handleErrorX` swallowed errors into `[]`, making a failed
   * request look identical to a legitimately empty response and
   * leaving the operator staring at an empty combobox with no
   * explanation.
   */
  private loadOrganizationsForRoles(): void {
    // `network` and `serviceProvider` roles are ONLY ever granted
    // through `cognito:groups`, which is exactly what populates
    // `startupService.roleOptions`. The JWT-embedded default
    // (`custom:role`/`custom:roleId`) can therefore never introduce
    // one of those two roles on its own, so `roleOptions` is the
    // complete source here — no union with the default role needed.
    const considered: Array<{ role: string; roleId: string }> = [];
    const seen = new Set<string>();
    for (const opt of this.startupService.roleOptions || []) {
      const role = opt && opt.role;
      const roleId = opt && opt.roleId;
      if (!role || !roleId) { continue; }
      const key = role + '|' + roleId;
      if (seen.has(key)) { continue; }
      seen.add(key);
      considered.push({ role, roleId });
    }

    const networkRoles = considered.filter(r => r.role === 'network');
    const serviceProviderRoles = considered.filter(r => r.role === 'serviceProvider');

    const rolesToLookup: Array<{ role: string; roleId: string }> = [];
    if (networkRoles.length > 0) {
      // Prefer network; skip the per-serviceProvider fan-out.
      for (const nr of networkRoles) { rolesToLookup.push(nr); }
    } else if (serviceProviderRoles.length > 0) {
      for (const spr of serviceProviderRoles) { rolesToLookup.push(spr); }
    }

    if (rolesToLookup.length === 0) {
      console.log('SelectRoleComponent: no network/serviceProvider roles held — skipping activeForRole lookup', considered);
      this.organizations = [];
      return;
    }

    this.loadingOrganizations = true;
    this.organizationsError = '';
    this.organizationLoadFailures = [];

    // Wrap each per-role observable so a single failure doesn't
    // collapse the whole forkJoin. On error we push a structured
    // failure record and emit an empty list from the wrapper so
    // forkJoin sees a "success" per branch.
    interface LookupResult {
      role: string;
      roleId: string;
      list: Array<ActiveOrgRow>;
      error?: string;
    }
    const wrapped: Array<Observable<LookupResult>> = rolesToLookup.map(r => {
      console.log(`SelectRoleComponent: requesting activeForRole ${r.role}/${r.roleId}`);
      return this.organizationService.getActiveOrganizationsForRole(r.role, r.roleId).pipe(
        map(list => ({ role: r.role, roleId: r.roleId, list })),
        catchError(err => {
          const message = this.formatHttpError(err);
          console.error(`SelectRoleComponent: activeForRole ${r.role}/${r.roleId} failed`, err);
          const failure: LookupResult = { role: r.role, roleId: r.roleId, list: [], error: message };
          return of(failure);
        })
      );
    });

    forkJoin(wrapped).subscribe(
      results => {
        // Merge & dedupe by ID; guard against nulls and malformed
        // rows returned by the server (missing name / bad ID).
        // `admin_ID` / `adminEmail` are propagated when present so
        // the autocomplete can render the primary user's email and
        // match against it during search. Older server builds may
        // omit them — treat as absent, everything else keeps
        // working.
        const byId = new Map<number, ActiveOrgRow>();
        for (const r of results) {
          if (!r || !Array.isArray(r.list)) { continue; }
          for (const row of r.list) {
            if (!row || typeof row.ID !== 'number' || row.ID <= 0) { continue; }
            if (byId.has(row.ID)) { continue; }
            byId.set(row.ID, this.normalizeActiveOrgRow(row));
          }
        }
        this.organizations = Array.from(byId.values())
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        const failed = results.filter(r => !!r.error);
        this.organizationLoadFailures = failed.map(f => ({
          role: f.role,
          roleId: f.roleId,
          label: `${this.labelFor(f.role)} #${f.roleId}`,
          message: f.error
        }));
        if (failed.length > 0) {
          this.organizationsError = failed.length === results.length
            ? 'Failed to load organizations. Click Retry.'
            : 'Some organizations could not be loaded. Click Retry.';
        } else {
          this.organizationsError = '';
        }
        this.loadingOrganizations = false;

        // Re-emit into the filter pipeline so the panel picks up the
        // newly loaded list without the user having to type first.
        this.organizationSearchControl.setValue(this.organizationSearchControl.value || '');
      },
      err => {
        // Should never actually reach here because every branch has
        // its own catchError above — but be defensive: if forkJoin
        // itself misbehaves, still surface something meaningful.
        this.loadingOrganizations = false;
        this.organizationsError = this.formatHttpError(err) || 'Failed to load organizations.';
        console.error('SelectRoleComponent.loadOrganizationsForRoles unexpected fatal error', err);
      }
    );
  }

  /**
   * Retry all currently-failed per-role lookups. Successful roles
   * are left alone — we don't re-hammer the endpoint for lists
   * that already came back cleanly.
   */
  retryOrganizationLoads(): void {
    if (!this.organizationLoadFailures || this.organizationLoadFailures.length === 0) { return; }
    const toRetry = this.organizationLoadFailures.slice();
    this.organizationLoadFailures = [];
    this.organizationsError = '';
    this.loadingOrganizations = true;

    interface LookupResult {
      role: string;
      roleId: string;
      list: Array<ActiveOrgRow>;
      error?: string;
    }
    const wrapped: Array<Observable<LookupResult>> = toRetry.map(r =>
      this.organizationService.getActiveOrganizationsForRole(r.role, r.roleId).pipe(
        map(list => ({ role: r.role, roleId: r.roleId, list })),
        catchError(err => of({ role: r.role, roleId: r.roleId, list: [], error: this.formatHttpError(err) } as LookupResult))
      )
    );

    forkJoin(wrapped).subscribe(results => {
      const byId = new Map<number, ActiveOrgRow>();
      for (const existing of this.organizations) { byId.set(existing.ID, existing); }
      for (const r of results) {
        if (!r || !Array.isArray(r.list)) { continue; }
        for (const row of r.list) {
          if (!row || typeof row.ID !== 'number' || row.ID <= 0) { continue; }
          if (byId.has(row.ID)) { continue; }
          byId.set(row.ID, this.normalizeActiveOrgRow(row));
        }
      }
      this.organizations = Array.from(byId.values())
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      const stillFailed = results.filter(r => !!r.error);
      this.organizationLoadFailures = stillFailed.map(f => ({
        role: f.role,
        roleId: f.roleId,
        label: `${this.labelFor(f.role)} #${f.roleId}`,
        message: f.error
      }));
      this.organizationsError = stillFailed.length > 0
        ? 'Some organizations still could not be loaded.'
        : '';
      this.loadingOrganizations = false;
      this.organizationSearchControl.setValue(this.organizationSearchControl.value || '');
    });
  }

  /**
   * Turn an `HttpErrorResponse` (or anything else that shows up in
   * an rxjs error slot) into a short operator-facing message.
   */
  private formatHttpError(err: any): string {
    if (!err) { return 'Unknown error'; }
    if (typeof err === 'string') { return err; }
    if (typeof err.status === 'number' && err.status > 0) {
      const statusText = err.statusText || '';
      return `HTTP ${err.status}${statusText ? ' ' + statusText : ''}`;
    }
    if (err.message) { return String(err.message); }
    return 'Network error';
  }

  /**
   * Coerce a raw server row into a well-typed `ActiveOrgRow`.
   * Ensures `name` is always a string, `serviceProvider_ID` is
   * always a number, and preserves the optional `admin_ID` /
   * `adminEmail` fields when the server provides them (added
   * 2026-08-06). Older server builds simply don't include them,
   * so we only copy them across when present and shaped
   * correctly — treating malformed values as absent rather than
   * fabricating something misleading.
   */
  private normalizeActiveOrgRow(row: any): ActiveOrgRow {
    const out: ActiveOrgRow = {
      ID: row.ID,
      name: typeof row.name === 'string' ? row.name : '',
      serviceProvider_ID: typeof row.serviceProvider_ID === 'number' ? row.serviceProvider_ID : 0
    };
    if (typeof row.admin_ID === 'number' && row.admin_ID > 0) {
      out.admin_ID = row.admin_ID;
    }
    if (typeof row.adminEmail === 'string' && row.adminEmail) {
      out.adminEmail = row.adminEmail;
    }
    return out;
  }

  private filterOrganizations(rawQuery: string): Array<ActiveOrgRow> {
    const query = (rawQuery || '').trim().toLowerCase();
    // Suppress the currently-active organization from the
    // suggestions. `StartupService.selectRole()` early-returns when
    // the requested (role, roleId) already matches the selected
    // one, so re-picking the current org would produce no
    // observable effect (no navigation, no data reload) and just
    // confuse the operator. Only applies when the current role IS
    // 'organization'; other current roles have no organization ID
    // to compare against.
    const suppressOrgId =
      this.startupService.selectedRole === 'organization'
        ? Number(this.startupService.selectedRoleId)
        : 0;
    const base = suppressOrgId > 0
      ? this.organizations.filter(o => o.ID !== suppressOrgId)
      : this.organizations;
    if (!query) { return base.slice(0, 100); }
    const results = base.filter(o => {
      if (o.name && o.name.toLowerCase().indexOf(query) !== -1) { return true; }
      if (String(o.ID).indexOf(query) !== -1) { return true; }
      // Match against the primary user's email when the server
      // included it (added 2026-08-06). Case-insensitive substring
      // match — same treatment as the name filter.
      if (o.adminEmail && o.adminEmail.toLowerCase().indexOf(query) !== -1) { return true; }
      return false;
    });
    return results.slice(0, 100);
  }

  displayOrganization = (o: ActiveOrgRow | null): string => {
    if (!o) { return ''; }
    // Suffix the primary user's email when available so the
    // operator's picked chip visibly confirms which admin they
    // matched. Falls back to `name (#ID)` for older server builds
    // (or organizations without an assigned admin).
    if (o.adminEmail) { return `${o.name} (#${o.ID}) — ${o.adminEmail}`; }
    return `${o.name} (#${o.ID})`;
  }

  onOrganizationSelected(org: ActiveOrgRow): void {
    this.selectedOrganization = org;
    // Reflect the selection into the input so users see what they picked.
    this.organizationSearchControl.setValue(this.displayOrganization(org), { emitEvent: false });
  }

  clearSelectedOrganization(): void {
    this.selectedOrganization = null;
    this.organizationSearchControl.setValue('');
  }

  /**
   * Called by the "Proceed" button once an organization is chosen
   * from the autocomplete. Same call the "supervised_user_circle"
   * button in `OrganizationComponent` makes to jump into an org.
   */
  proceedIntoSelectedOrganization(): void {
    if (!this.selectedOrganization) { return; }
    this.startupService.selectRole(
      'organization',
      '' + this.selectedOrganization.ID,
      this.selectedOrganization.name
    );
  }

  // ───────────────────────────────────────────────────────
  // Template plumbing
  // ───────────────────────────────────────────────────────

  /**
   * True when the autocomplete section should render.
   *
   * Only `roleOptions` is consulted: `network` / `serviceProvider`
   * are always group-derived, so the JWT default role can never be
   * one of them.
   */
  get shouldShowOrganizationPicker(): boolean {
    const opts = this.startupService.roleOptions || [];
    return opts.some(r => r.role === 'network' || r.role === 'serviceProvider');
  }
}














